import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { BreadcrumbSchema } from "@/components/StructuredData";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle, 
  ArrowRight, 
  FileCheck, 
  Shield, 
  MessageSquare, 
  Clock, 
  MapPin, 
  PoundSterling 
} from "lucide-react";

const ARCH_BREADCRUMBS = [
  { name: "Home", url: "/" },
  { name: "For Architects", url: "/for-architects" }
];

const ForArchitectsPage = () => {
  const services = [
    {
      id: "arch-service-extensions",
      title: "Extensions & Conversions",
      description: "Single and double storey extensions, loft and garage conversions — built to specification."
    },
    {
      id: "arch-service-refurbishments",
      title: "Refurbishments",
      description: "Full property refurbishment and renovation projects — delivered to a high standard."
    },
    {
      id: "arch-service-carpentry",
      title: "Carpentry & Joinery",
      description: "Bespoke and structural carpentry, first and second fix — precise, clean, crafted with care."
    },
    {
      id: "arch-service-kitchen",
      title: "Kitchen Installations",
      description: "Full kitchen fitting, worktops, finishing work — aligned to design intent."
    },
    {
      id: "arch-service-bathroom",
      title: "Bathroom Installations",
      description: "Complete bathroom builds — plumbing, waterproofing, tiling, finishing."
    },
    {
      id: "arch-service-general",
      title: "General Building",
      description: "Groundworks, brickwork, plastering, decorating — to complete the full scope."
    }
  ];

  const reasons = [
    {
      id: "arch-reason-design",
      icon: FileCheck,
      title: "Your design, built right",
      description: "We read drawings carefully, ask upfront questions, and execute to specification. No shortcuts or interpretation errors."
    },
    {
      id: "arch-reason-reputation",
      icon: Shield,
      title: "Your reputation is safe with us",
      description: "We understand your name is on the line — we protect it with quality work that reflects well on you."
    },
    {
      id: "arch-reason-communicate",
      icon: MessageSquare,
      title: "We communicate clearly",
      description: "We keep you informed at key stages, flag needs for input early — no chasing required."
    },
    {
      id: "arch-reason-turnup",
      icon: Clock,
      title: "We turn up",
      description: "Reliable attendance, respect for programme, clear timelines — no mid-project disappearances."
    },
    {
      id: "arch-reason-local",
      icon: MapPin,
      title: "Local knowledge of planning and regs",
      description: "Deep understanding of Surrey and SW London building regulations — no site surprises."
    },
    {
      id: "arch-reason-pricing",
      icon: PoundSterling,
      title: "Competitive, transparent pricing",
      description: "Detailed breakdowns, no hidden costs — straightforward cost management."
    }
  ];

  const coverageAreas = [
    "Epsom", "Ewell", "Sutton", "Cheam", "Banstead", 
    "Carshalton", "Wallington", "Croydon", "Kingston", "Wimbledon"
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO 
        title="Builder for Architects in Surrey"
        description="TJP Construction partners with architects across Surrey & SW London. Reliable build partner for residential projects. Quality workmanship, on-time delivery."
        keywords="architect contractor surrey, building partner architects, residential construction sw london, specification building"
        url="/for-architects"
      />
      <BreadcrumbSchema items={ARCH_BREADCRUMBS} />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <p className="text-amber-500 font-medium tracking-wide uppercase text-sm mb-4">
              For Architects
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6 leading-tight">
              A Build Partner Who Takes Your Designs Seriously
            </h1>
            <p className="text-xl text-stone-300 mb-8 leading-relaxed">
              Skilled, reliable carpentry and construction across Surrey and SW London — 
              delivered with the quality and professionalism your clients expect.
            </p>
            <Link to="/contact">
              <Button 
                className="bg-amber-600 text-white hover:bg-amber-500 h-14 px-10 font-bold text-base shadow-lg"
                data-testid="architect-hero-cta"
              >
                Get in Touch
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6">
              A Contractor You Can Trust
            </h2>
            <p className="text-lg text-stone-600 leading-relaxed mb-6">
              Architects need a trustworthy contractor who understands their designs, 
              communicates effectively, and upholds their reputation.
            </p>
            <p className="text-lg text-stone-600 leading-relaxed">
              TJP delivers high-quality residential work across Surrey and South West London. 
              We act as a genuine build partner with clear communication, protecting your reputation, 
              and executing to specification. Whether it's a single project or an ongoing pipeline — we're ready.
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-wide uppercase text-amber-700 mb-4">
              What We Build
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Full Scope Residential Construction
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              We cover the full scope of residential construction work.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <div 
                key={service.id}
                className="bg-stone-50 rounded-lg p-8 border border-stone-100 hover:border-amber-200 transition-colors"
              >
                <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-stone-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Architects Work With TJP */}
      <section className="py-20 md:py-28 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-wide uppercase text-amber-500 mb-4">
              Why Work With Us
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">
              Why Architects Work With TJP
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reasons.map((reason) => (
              <div 
                key={reason.id}
                className="bg-slate-800/50 rounded-lg p-8 border border-slate-700"
              >
                <div className="w-12 h-12 bg-amber-600/20 rounded-lg flex items-center justify-center mb-6">
                  <reason.icon className="text-amber-500" size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{reason.title}</h3>
                <p className="text-stone-400 leading-relaxed">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Area */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-medium tracking-wide uppercase text-amber-700 mb-4">
                Where We Work
              </p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6">
                Coverage Area
              </h2>
              <p className="text-lg text-stone-600 leading-relaxed mb-6">
                We serve the <strong>SM, CR, and SW postcode areas</strong> across Surrey and South West London.
              </p>
              <div className="flex flex-wrap gap-3">
                {coverageAreas.map((area) => (
                  <span 
                    key={`arch-area-${area.toLowerCase().replace(/\s+/g, '-')}`}
                    className="bg-stone-100 text-stone-700 px-4 py-2 rounded-full text-sm font-medium"
                  >
                    {area}
                  </span>
                ))}
              </div>
              <p className="text-stone-500 mt-4 text-sm">
                And surrounding areas across Surrey and South West London
              </p>
            </div>
            <div className="bg-slate-900 rounded-lg p-8 text-center">
              <MapPin className="text-amber-500 mx-auto mb-4" size={48} />
              <h3 className="text-2xl font-bold text-white mb-2">Local Expertise</h3>
              <p className="text-stone-400">
                Deep knowledge of Surrey & SW London planning requirements and building regulations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-amber-700">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Get in Touch
          </h2>
          <p className="text-xl text-amber-100 mb-8 leading-relaxed">
            If you're looking for a reliable, quality-focused contractor for your next 
            residential project, we'd love to hear from you. Ready to discuss a project?
          </p>
          <Link to="/contact">
            <Button 
              className="bg-white text-amber-700 hover:bg-amber-50 h-14 px-10 font-bold text-base shadow-lg"
              data-testid="architect-footer-cta"
            >
              Contact Tony Directly
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ForArchitectsPage;
